<template>
    <div
        :class="{ popover: !!position, [`popover-${position}`]: true, active: picking }"
        class="base-date-input hover-disabled"
    >
        <input-group
            glyph="calendar"
            glyph-position="right"
            @click="click_icon"
        >
            <template v-slot:left_addon><slot name="left_addon"></slot></template>
            <base-input
                ref="field"
                :id="identifier"
                type="text"
                :name="name"
                :placeholder="placeholder"
                :value="display_value"
                :aria-describedby="!!has_slot('hint')?`${identifier}-description`:false"
                :disabled="disabled"
                :required="required"
                @focus="focused"
                @touchstart="touchstart"
                @keyup="$emit('keyup', $event)"
                @keydown="keydown"
                @blur="blurred"
            />
        </input-group>
        <div
            v-if="!!position"
            ref="calendar"
            class="popover-container"
        >
            <base-calendar
                :min="min"
                :max="max"
                :selection="value"
                :size="size"
                @click="pick"
            />
        </div>
    </div>
</template>

<script>
import debounce from "lodash/debounce"
import {
    format
} from "date-fns"

import is_nibnut_component from "@/nibnut/mixins/IsNibnutComponent"
import is_alpha_numerical_input from "@/nibnut/mixins/IsAlphaNumericalInput"
import handles_dates from "@/nibnut/mixins/HandlesDates"

import InputGroup from "./InputGroup"
import BaseInput from "./BaseInput"
import BaseCalendar from "./BaseCalendar"

const chrono = require("chrono-node")

export default {
    name: "BaseDateInput",
    mixins: [is_nibnut_component, is_alpha_numerical_input, handles_dates],
    components: {
        InputGroup,
        BaseInput,
        BaseCalendar
    },
    watch: {
        editable: "maybe_focus_field"
    },
    methods: {
        focus_field () {
            this.$refs.field.$el.focus()
            // this.$refs.field.$el.select()
        },
        maybe_focus_field () {
            if(this.editable) setTimeout(this.focus_field, 150)
        },
        focused () {
            this.$refs.field.$el.select()
            this.picking = true
        },
        keydown: debounce(function () {
            if(this.alphanumeric && this.iOS) this.touching = false
        }, 300),
        blurred (event) {
            if(this.alphanumeric && this.iOS) this.touching = false

            const is_calendar_clicked = !!event.relatedTarget && !!this.$refs.calendar && this.$refs.calendar.contains(event.relatedTarget)
            if(!is_calendar_clicked) {
                let date
                try {
                    date = chrono.parseDate(event.target.value)
                    if(isNaN(date.getTime())) date = null
                } catch (error) {
                    date = null
                }
                if(!date || (format(this.standardized_date(this.value), this.dateFormat) !== format(date, this.dateFormat))) this.$emit("input", date, this.name)
                this.picking = false
            }
        },
        click_icon () {
            this.focus_field()
        },
        pick (date) {
            this.$emit("input", date, this.name)
            if(this.closeOnClick) this.picking = false
        }
    },
    computed: {
        display_value () {
            if(!this.value) return ""
            return format(this.standardized_date(this.value), this.dateFormat)
        }
    },
    props: {
        id: {
            type: String,
            validator: prop => !!prop
        },
        name: {
            type: String,
            validator: prop => !!prop,
            required: true
        },
        placeholder: {
            type: String,
            default: ""
        },
        value: {
            default: ""
        },
        dateFormat: {
            type: String,
            default: "MM/dd/yyyy"
        },
        position: { // use "over" for dialogs, where the popup spills out: "over" will place the calendar popup "over" the field/trigger
            validator: prop => {
                return !prop || (typeof prop === "string") || !!prop.match(/^(top|bottom|left|right|over)$/i)
            },
            default: "top"
        },
        min: { // null, string or native date
            default: null
        },
        max: { // null, string or native date
            default: null
        },
        size: {
            type: String,
            validator: prop => !!prop && prop.match(/^(sm|md|lg)$/i),
            default: "md"
        },
        closeOnClick: {
            type: Boolean,
            default: true
        },
        editable: {
            type: Boolean,
            default: true
        },
        required: {
            type: Boolean,
            required: true
        },
        disabled: {
            type: Boolean,
            default: false
        },
        saving: {
            type: Boolean,
            default: false
        },
        error: {
            type: String,
            default: ""
        }
    },
    data () {
        return {
            picking: false
        }
    }
}
</script>

<style lang="scss">
.base-date-input {
    width: 100%;
}
</style>
